import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { refreshBhajanDataRequest } from '../../Features/BhajanSlice';
import { setLanguage } from '../../Features/LanguageSlice';
import { APP } from '../../app/appConfig';
import { navFor } from '../../app/appPolicy';
import { cn } from '../../ui';

const LANGUAGES = [
  'English', 'Telugu', 'Kannada', 'Tamil', 'Hindi',
  'Malayalam', 'Oriya', 'Bengali', 'Punjabi', 'Gujarati',
];

const Header: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const selectedLanguage = useSelector((s: RootState) => s.language.selectedLanguage);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  const navItems = navFor(APP, selectedLanguage);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const onPickLanguage = (lang: string) => {
    setLangOpen(false);
    dispatch(setLanguage(lang));
    dispatch(refreshBhajanDataRequest(lang));
  };

  return (
    <header
      role="banner"
      className="sticky top-0 z-[800] border-b border-light"
      style={{ background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur)' }}
    >
      <a href="#main" className="skip-link">Skip to content</a>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 min-w-0 group">
          <img src={APP.brand.logoSrc} alt="" className="h-9 w-9 shrink-0 rounded-full ring-1 ring-accent-gold/30 group-hover:ring-accent-gold/60 transition-all" />
          <span className="font-display text-h3 font-semibold tracking-tight truncate">{APP.brand.wordmark}</span>
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-1 shrink-0">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                cn(
                  'px-2 xl:px-3 py-2 rounded-md text-body font-medium whitespace-nowrap transition-colors duration-150',
                  isActive
                    ? 'text-accent-gold bg-white/5'
                    : 'text-text-secondary hover:text-text-primary hover:bg-white/5'
                )
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <div className="relative" ref={dropRef}>
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={langOpen}
              aria-label={`Language: ${selectedLanguage || 'English'}`}
              className="flex items-center justify-center gap-2 h-10 w-10 sm:w-auto sm:px-3 rounded-md border border-light bg-bg-3 text-text-secondary hover:text-text-primary hover:border-strong transition-colors"
            >
              <GlobeIcon />
              <span className="hidden sm:inline font-medium">{selectedLanguage || 'English'}</span>
              <ChevronIcon className={cn('hidden sm:block transition-transform', langOpen && 'rotate-180')} />
            </button>
            {langOpen && (
              <div
                role="listbox"
                aria-label="Choose language"
                className="absolute right-0 mt-2 w-64 rounded-xl border border-light shadow-4 overflow-hidden"
                style={{ background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur)' }}
              >
                <div className="px-4 py-3 border-b border-light">
                  <div className="text-small font-semibold">Select language</div>
                  <div className="text-[12px] text-text-tertiary">Lyrics + interface</div>
                </div>
                <div className="p-2 grid grid-cols-2 gap-1 max-h-72 overflow-y-auto">
                  {LANGUAGES.map((lang) => {
                    const active = selectedLanguage === lang;
                    return (
                      <button
                        key={lang}
                        role="option"
                        aria-selected={active}
                        onClick={() => onPickLanguage(lang)}
                        className={cn(
                          'flex items-center justify-between px-3 py-2 rounded-md text-small font-medium text-left transition-colors',
                          active ? 'bg-accent-gold text-bg-0' : 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
                        )}
                      >
                        <span>{lang}</span>
                        {active && <CheckIcon />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-md border border-light bg-bg-3 text-text-secondary hover:text-text-primary"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav aria-label="Primary mobile" className="lg:hidden border-t border-light px-4 py-2">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                cn(
                  'block px-3 py-3 rounded-md text-body font-medium',
                  isActive ? 'text-accent-gold bg-white/5' : 'text-text-secondary hover:text-text-primary hover:bg-white/5'
                )
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
};

const GlobeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);
const ChevronIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="18" x2="18" y2="6" />
  </svg>
);

export { Header };
export default Header;
